"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.putFenceReasonMapSchema = exports.fenceReasonMapEntryInputSchema = exports.fenceImportSchema = exports.fenceImportRowSchema = exports.ELIGIBILITY_FENCE_COMPARISON_CLASSES = exports.ELIGIBILITY_FENCE_PARTNER = exports.eligibilityTestRunCompareQuerySchema = exports.setEligibilityTestRunExpectationsSchema = exports.eligibilityTestRunExpectationEntrySchema = exports.eligibilityExpectedVerdictSchema = exports.previewEligibilityTestRunCohortSchema = exports.createEligibilityTestRunSchema = exports.eligibilityTestRunRulesetSourceSchema = exports.eligibilityTestRunCohortSchema = exports.eligibilityTestRunFenceStateSchema = exports.ELIGIBILITY_TEST_RUN_MAX_ROWS = void 0;
exports.isFenceCheckedPartner = isFenceCheckedPartner;
const zod_1 = require("zod");
const rule_schema_1 = require("../eligibility/rule-schema");
const eligibility_1 = require("./eligibility");
const dateOnly = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, 'date must be ISO YYYY-MM-DD');
exports.ELIGIBILITY_TEST_RUN_MAX_ROWS = 200;
exports.eligibilityTestRunFenceStateSchema = zod_1.z.enum(['pending', 'submitted']);
exports.eligibilityTestRunCohortSchema = zod_1.z
    .object({
    partner: zod_1.z.string().trim().min(1).max(80).optional(),
    open_only: zod_1.z.boolean().optional(),
    created_from: dateOnly.optional(),
    created_to: dateOnly.optional(),
    fence_state: exports.eligibilityTestRunFenceStateSchema.optional(),
    bl_order_names: zod_1.z
        .array(zod_1.z.string().trim().min(1).max(255))
        .max(exports.ELIGIBILITY_TEST_RUN_MAX_ROWS)
        .default([]),
})
    .strict()
    .refine((cohort) => cohort.partner !== undefined ||
    cohort.open_only === true ||
    cohort.created_from !== undefined ||
    cohort.created_to !== undefined ||
    cohort.fence_state !== undefined ||
    cohort.bl_order_names.length > 0, { message: 'at least one cohort selector is required', path: ['bl_order_names'] })
    .refine((cohort) => cohort.created_from === undefined ||
    cohort.created_to === undefined ||
    cohort.created_from <= cohort.created_to, { message: 'created_from must be on or before created_to', path: ['created_from'] });
exports.eligibilityTestRunRulesetSourceSchema = zod_1.z.enum(['live', 'draft']);
exports.createEligibilityTestRunSchema = zod_1.z
    .object({
    // partner is a plain string here. The service checks membership against the Prisma enum instead.
    partner: zod_1.z.string().trim().min(1).max(80),
    cohort: exports.eligibilityTestRunCohortSchema,
    as_of: dateOnly.optional(),
    ruleset_source: exports.eligibilityTestRunRulesetSourceSchema.default('live'),
    draft_ruleset: zod_1.z
        .array(rule_schema_1.eligibilityRuleInputSchema)
        .max(exports.ELIGIBILITY_TEST_RUN_MAX_ROWS)
        .optional(),
})
    .strict()
    .superRefine((input, ctx) => {
    if (input.ruleset_source === 'draft' && input.draft_ruleset === undefined) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'draft_ruleset is required when ruleset_source is draft',
            path: ['draft_ruleset'],
        });
    }
    if (input.ruleset_source === 'live' && input.draft_ruleset !== undefined) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'draft_ruleset is only valid when ruleset_source is draft',
            path: ['draft_ruleset'],
        });
    }
});
exports.previewEligibilityTestRunCohortSchema = exports.eligibilityTestRunCohortSchema;
exports.eligibilityExpectedVerdictSchema = zod_1.z.enum([
    'eligible',
    'partially_eligible',
    'not_eligible',
    'unsure',
]);
exports.eligibilityTestRunExpectationEntrySchema = zod_1.z
    .object({
    bl_order_id: eligibility_1.eligibilityBlOrderIdSchema,
    expected_verdict: exports.eligibilityExpectedVerdictSchema,
    expected_note: zod_1.z.string().trim().max(2000).nullable().optional(),
})
    .strict();
exports.setEligibilityTestRunExpectationsSchema = zod_1.z
    .object({
    expectations: zod_1.z
        .array(exports.eligibilityTestRunExpectationEntrySchema)
        .min(1)
        .max(exports.ELIGIBILITY_TEST_RUN_MAX_ROWS),
})
    .strict();
exports.eligibilityTestRunCompareQuerySchema = zod_1.z
    .object({ against: zod_1.z.string().uuid() })
    .strict();
// Fence checks only Rivonia deals, so every Fence surface here applies only to Rivonia runs.
exports.ELIGIBILITY_FENCE_PARTNER = 'Rivonia';
function isFenceCheckedPartner(partner) {
    return partner === exports.ELIGIBILITY_FENCE_PARTNER;
}
// These comparison classes are computed at attach time. A row stays null when unjoined, out of cohort, or not comparable; those cases appear as separate counts in EligibilityFenceMetrics instead.
exports.ELIGIBILITY_FENCE_COMPARISON_CLASSES = [
    'agree_accept',
    'agree_reject',
    'mismatch_we_accept',
    'mismatch_we_reject',
    'out_of_scope_rejection',
    'unmapped',
    'fence_no_verdict',
];
exports.fenceImportRowSchema = zod_1.z
    .object({
    external_id: zod_1.z.string().trim().min(1).max(255),
    is_eligible: zod_1.z.boolean().nullable(),
    reasons: zod_1.z.array(zod_1.z.string().trim().min(1).max(255)).max(50).default([]),
    fence_timestamp: zod_1.z.string().datetime().optional(),
})
    .strict();
exports.fenceImportSchema = zod_1.z
    .object({
    rows: zod_1.z.array(exports.fenceImportRowSchema).min(1).max(exports.ELIGIBILITY_TEST_RUN_MAX_ROWS),
})
    .strict();
// Exactly one of maps_to_fact_name or not_checked_by_us can be set, matching the CHECK constraint on eligibility_fence_reason_map.
exports.fenceReasonMapEntryInputSchema = zod_1.z
    .object({
    fence_reason_key: zod_1.z.string().trim().min(1).max(255),
    maps_to_fact_name: zod_1.z.string().trim().min(1).max(255).nullable().optional(),
    not_checked_by_us: zod_1.z.boolean().optional().default(false),
})
    .strict()
    .refine((entry) => {
    const hasFact = entry.maps_to_fact_name !== null && entry.maps_to_fact_name !== undefined;
    return hasFact !== entry.not_checked_by_us;
}, {
    message: 'exactly one of maps_to_fact_name or not_checked_by_us must be set',
    path: ['maps_to_fact_name'],
});
exports.putFenceReasonMapSchema = zod_1.z
    .object({
    entries: zod_1.z.array(exports.fenceReasonMapEntryInputSchema).min(1).max(500),
})
    .strict();
//# sourceMappingURL=eligibility-test-runs.js.map